// src/modules/leads/pages/LeadStatusesAdmin.jsx
// Admin-configurable lead statuses / interest levels (Hot, Cold, …). Drives the Status dropdown.
import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Space, Tag, message, Typography, Popconfirm } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { fetchLeadStatuses, createLeadStatus, updateLeadStatus, deleteLeadStatus } from '../api';

const { Title } = Typography;

const COLOR_OPTIONS = [
  'red', 'volcano', 'orange', 'gold', 'lime', 'green', 'cyan', 'blue',
  'geekblue', 'purple', 'magenta', 'default',
];

export default function LeadStatusesAdmin() {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await fetchLeadStatuses(); setStatuses(r.data.data || []); }
    catch (err) { message.error(err?.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); form.resetFields(); form.setFieldsValue({ color: 'blue' }); setOpen(true); };
  const openEdit = (row) => { setEditing(row); form.setFieldsValue(row); setOpen(true); };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      if (editing) await updateLeadStatus(editing._id, v);
      else await createLeadStatus(v);
      message.success(editing ? 'Updated' : 'Created');
      setOpen(false); load();
    } catch (err) {
      if (!err?.errorFields) message.error(err?.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const columns = [
    { title: 'Code', dataIndex: 'code', render: (v) => <Tag>{v}</Tag> },
    { title: 'Name', dataIndex: 'name', render: (v, r) => <Tag color={r.color || 'default'}>{v}</Tag> },
    { title: 'Description', dataIndex: 'description', render: (v) => v || '—' },
    { title: 'Active', dataIndex: 'isActive', render: (v) => v ? <Tag color="success">Active</Tag> : <Tag>Inactive</Tag> },
    {
      title: '', render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
          {r.isActive && <Popconfirm title="Deactivate this status?" onConfirm={async () => { await deleteLeadStatus(r._id); message.success('Deactivated'); load(); }}><Button size="small" danger>Deactivate</Button></Popconfirm>}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Title level={4} style={{ margin: 0 }}>Lead Statuses</Title>
        <Space>
          <Button icon={<PlusOutlined />} type="primary" onClick={openNew}>New Status</Button>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        </Space>
      </Space>
      <Card size="small">
        <Table rowKey="_id" size="small" columns={columns} dataSource={statuses} loading={loading} pagination={false} />
      </Card>

      <Modal title={editing ? 'Edit Lead Status' : 'New Lead Status'} open={open} onCancel={() => setOpen(false)} onOk={submit} okButtonProps={{ loading: saving }} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="code" label="Code" rules={[{ required: true }]}><Input disabled={!!editing} placeholder="e.g. HOT" /></Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input placeholder="e.g. Hot" /></Form.Item>
          <Form.Item name="color" label="Tag Colour">
            <Select options={COLOR_OPTIONS.map((c) => ({ value: c, label: <Tag color={c}>{c}</Tag> }))} />
          </Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
